<script setup lang="ts">
import { ref, onUnmounted } from 'vue'

interface Props {
  modelValue: boolean
  title?: string
  initialX?: number
  initialY?: number
  initialWidth?: number
  initialHeight?: number
  zIndex?: number
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Videocall',
  initialX: 250,
  initialY: 100,
  initialWidth: 720,
  initialHeight: 420,
  zIndex: 9999,
})

const emit = defineEmits(['update:modelValue', 'drag-start', 'drag-end', 'resize'])

const posX = ref(props.initialX)
const posY = ref(props.initialY)
const width = ref(props.initialWidth)
const height = ref(props.initialHeight)

const dragging = ref(false)
const resizing = ref(false)

const offsetX = ref(0)
const offsetY = ref(0)
const startX = ref(0)
const startY = ref(0)
const startW = ref(0)
const startH = ref(0)

/* DRAG */
const startDrag = (e: MouseEvent) => {
  dragging.value = true
  offsetX.value = e.clientX - posX.value
  offsetY.value = e.clientY - posY.value
  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', stopDrag)
}

const onDrag = (e: MouseEvent) => {
  if (!dragging.value) return
  posX.value = Math.max(0, e.clientX - offsetX.value)
  posY.value = Math.max(0, e.clientY - offsetY.value)
}

const stopDrag = () => {
  dragging.value = false
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
}

/* RESIZE */
const startResize = (e: MouseEvent) => {
  resizing.value = true
  startW.value = width.value
  startH.value = height.value
  startX.value = e.clientX
  startY.value = e.clientY
  document.addEventListener('mousemove', onResize)
  document.addEventListener('mouseup', stopResize)
  e.preventDefault()
}

const onResize = (e: MouseEvent) => {
  if (!resizing.value) return
  width.value = Math.max(420, startW.value + (e.clientX - startX.value))
  height.value = Math.max(280, startH.value + (e.clientY - startY.value))
  emit('resize', { width: width.value, height: height.value })
}

const stopResize = () => {
  resizing.value = false
  document.removeEventListener('mousemove', onResize)
  document.removeEventListener('mouseup', stopResize)
}

onUnmounted(() => {
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
  document.removeEventListener('mousemove', onResize)
  document.removeEventListener('mouseup', stopResize)
})
</script>

<template>
  <div
    v-if="modelValue"
    class="fixed rounded-2xl shadow-2xl bg-black overflow-hidden flex flex-col"
    :style="{
      top: posY + 'px',
      left: posX + 'px',
      width: width + 'px',
      height: height + 'px',
      zIndex,
    }"
  >
    <div
      class="h-10 px-4 flex items-center justify-between bg-black/70 text-white cursor-move shrink-0 select-none"
      @mousedown="startDrag"
    >
      <div class="flex items-center gap-2 text-sm font-medium">
        {{ title }}
      </div>
      <span class="text-xs opacity-70">00:45:32</span>
    </div>

    <div class="flex-1 min-h-0 relative">
      <div
        class="grid h-full"
        style="grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));"
      >
        <div class="relative">
          <img
            src="https://images.unsplash.com/photo-1544005313-94ddf0286df2"
            class="absolute inset-0 w-full h-full object-cover"
          />
          <div class="absolute bottom-3 left-3 px-3 py-1 rounded-full bg-black/60 text-white text-sm">
            Anna
          </div>
        </div>

        <div class="relative">
          <img
            src="https://images.unsplash.com/photo-1527980965255-d3b416303d12"
            class="absolute inset-0 w-full h-full object-cover"
          />
          <div class="absolute bottom-3 left-3 px-3 py-1 rounded-full bg-black/60 text-white text-sm">
            Ben
          </div>
        </div>
      </div>

      <div class="absolute top-4 right-4">
        <img
          src="https://images.unsplash.com/photo-1544005313-94ddf0286df2"
          class="w-12 h-12 rounded-full border-2 border-white shadow-lg"
        />
      </div>
    </div>

    <div
      class="absolute bottom-1 right-1 w-4 h-4 cursor-se-resize"
      @mousedown="startResize"
    >
      <svg class="w-3 h-3 text-white/70" viewBox="0 0 12 12">
        <path fill="currentColor" d="M12 0L12 12L0 12Z" />
      </svg>
    </div>
  </div>
</template>
